import { useCallback, useEffect, useMemo, useState } from "react";
import { AUTH_STORAGE_KEYS } from "../api/authApi";
import { UnauthorizedError, fetchExpiringKeys, fetchPartners } from "../api/licencesApi";

const PAGE_SIZE = 10;

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function getPartnerDiscountPercent(partners, partnerId) {
  if (!partnerId || !Array.isArray(partners)) return 0;
  const pid = String(partnerId);
  const partner = partners.find((p) => {
    if (!p || typeof p !== "object") return false;
    const candidate =
      p.partnerId ??
      p.PartnerId ??
      p.PartnerID ??
      p.ResellerID ??
      p.resellerId ??
      p.ID ??
      p.Id ??
      p.id;
    return candidate != null && String(candidate) === pid;
  });
  return Math.max(0, toNumber(partner?.DiscountPercent));
}

export function useLicences({ onUnauthorized }) {
  const [licences, setLicences] = useState([]);
  const [partners, setPartners] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const partnerId = localStorage.getItem(AUTH_STORAGE_KEYS.partnerId);

  const refresh = useCallback(async () => {
    if (!partnerId) {
      onUnauthorized();
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [items, partnersResult] = await Promise.all([
        fetchExpiringKeys(partnerId),
        fetchPartners(partnerId),
      ]);
      setLicences(items);
      setPartners(Array.isArray(partnersResult) ? partnersResult : []);
      setPage(1);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      console.error(err);
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [partnerId, onUnauthorized]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const totalPages = Math.max(1, Math.ceil(licences.length / PAGE_SIZE));
  const pageItems = licences.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const discountPercent = useMemo(() => {
    return getPartnerDiscountPercent(partners, partnerId);
  }, [partners, partnerId]);

  const goToPage = useCallback(
    (p) => setPage(Math.min(Math.max(1, p), totalPages)),
    [totalPages],
  );

  return {
    pageItems,
    page,
    totalPages,
    partners,
    discountPercent,
    loading,
    error,
    setError,
    goToPage,
    refresh,
  };
}
